import React, { useState } from 'react';
//@ts-ignore
import * as styles from './on-page-search.module.css';
import * as autocompletestyles from './autocomplete.module.css';
import { InstantSearch, connectAutoComplete } from 'react-instantsearch-dom';

import AutoSuggest from '../Global/LazyAutoSuggest';
import { navigate } from 'gatsby-link';
import { OnPageSupportProps } from '.';
import Button from '../Global/Button';
import { searchClient } from '../Global/DataUtils/algolia';
import {
    getAlgoliaIndexPrefix,
    getAlgoliaLangSuffix,
} from '../../global/utility';

interface Props extends OnPageSupportProps {
    preferredLanguage?: string;
}

const OnPageSupportSearch: React.FC<Props> = ({
    preferredLanguage = 'default',
    headline,
    cta_label,
    hint_text,
    search_term_subheadline,
    search_results_page,
    kontentItemId,
}) => {
    const [searchTerm, setSearchTerm] = useState('');
    const [formError, setFormError] = useState('');
    const algoliaIndexPrefix = getAlgoliaIndexPrefix(
        process.env.GATSBY_ALGOLIA_ENV
    );
    // Algolia index suffixes are case-sensitive (e.g. `_GB-en`, `_AU-en`)
    // while preferredLanguage may be lowercased — denormalize before use.
    let indexName = `${algoliaIndexPrefix}ProductSupportPages_${getAlgoliaLangSuffix(
        preferredLanguage
    )}`;
    const searchResultsPage =
        search_results_page?.value &&
        search_results_page?.value[0]?.elements?.slug?.url;

    const handleAutoCompleteChange = (
        event: React.ChangeEvent<HTMLInputElement>,
        { newValue }
    ) => {
        if (formError && newValue) {
            setFormError('');
        }
        setSearchTerm(newValue);
    };

    const handleFormSubmit = (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        if (searchTerm) {
            // redirect to search page if search text entered
            if (searchResultsPage) {
                navigate(searchResultsPage, {
                    state: { searchTerm },
                });
            }
        }

        if (!searchTerm) {
            setFormError('Please enter search term.');
        }
    };

    return (
        <section
            className={styles.onPageSearch}
            data-kontent-item-id={kontentItemId}
        >
            <InstantSearch indexName={indexName} searchClient={searchClient}>
                <div className={styles.formPanel}>
                    <h2 data-kontent-element-codename="headline">
                        {headline.value}
                    </h2>
                    <div className={styles.formPanelContent}>
                        <form onSubmit={handleFormSubmit}>
                            <div className={styles.formItem}>
                                <label
                                    htmlFor="search"
                                    data-kontent-element-codename="search_term_subheadline"
                                >
                                    {search_term_subheadline.value}
                                </label>
                                <div className={styles.autocomplete}>
                                    <AutoComplete
                                        onChange={handleAutoCompleteChange}
                                        placeholder={hint_text.value}
                                        value={searchTerm}
                                    />
                                </div>
                            </div>
                            <div className={styles.formActions}>
                                <Button type="submit" visualStyle="outlineDark">
                                    {cta_label?.value}
                                </Button>
                            </div>
                        </form>
                        {formError && (
                            <p className={styles.formError}>{formError}</p>
                        )}
                    </div>
                </div>
            </InstantSearch>
        </section>
    );
};

const AutoComplete = connectAutoComplete(({ hits, refine, ...props }) => {
    const onSuggestionSelected = (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
    };

    return (
        <AutoSuggest
            theme={autocompletestyles}
            suggestions={hits}
            onSuggestionsFetchRequested={({ value }) => refine(value)}
            onSuggestionsClearRequested={() => refine('')}
            onSuggestionSelected={onSuggestionSelected}
            getSuggestionValue={(hit) => hit.display_page_name}
            renderSuggestion={(hit) => <p>{hit.display_page_name}</p>}
            inputProps={{
                placeholder: props.placeholder,
                value: props.value,
                onChange: (
                    suggestChangeEvent: React.ChangeEvent<HTMLInputElement>,
                    rest
                ) => {
                    props.onChange(suggestChangeEvent, rest);
                },
            }}
        />
    );
});

export default OnPageSupportSearch;
